/**
 * 69.6h (the owner: "Also want to add the ability to pick respawn of options for player, but don't want to make them
 * re-select every time"): a human seat picks where the units it holds come back — its side's base, or one of its relays
 * or FOBs — once; the Match keeps the pick per seat for the match (a `respawnAt` input, logged, so replays agree) and
 * honours it at every respawn of a unit the seat holds while the place stands: the base always; a relay standing, linked
 * and with a drop left (it spends one, as a drop does); a FOB standing and linked; never in the sector in play (69.6f).
 * Lost or cut, the unit comes back by the rule as before, and the pick stays for when it stands again. Bots never pick.
 */
import type { GameState, PlayerState, StructureState, Vec3 } from '../types';
import { structureTeam } from './body';
import { isLinked } from './web';
import { spawnSiteRefusal } from './build';
import { beaconArrival } from './beacon';
import { battlePlanetIndex } from './actionSetup';

/** Where a seat's units come back: its side's base, or one of its relays or FOBs. */
export type RespawnPick = 'base' | { structureId: number };

/** A place the seat may pick, for the table: its kind, where it is, and whether a respawn there would be honoured now. */
export interface RespawnPlace { kind: 'base' | 'relay' | 'fob'; structureId?: number; planetIndex: number; surfaceDir: Vec3; open: boolean }

/** The side's base place: the Foothold board's, else where the side stood at the start; null with neither. */
function basePlace(state: GameState, team: number): { planetIndex: number; surfaceDir: Vec3 } | null {
  const b = state.board?.bases.find(x => x.team === team);
  const bi = battlePlanetIndex(state);
  if (b && bi >= 0) return { planetIndex: bi, surfaceDir: b.dir };
  const home = state.actionHomes?.[team];
  return home && bi >= 0 ? { planetIndex: bi, surfaceDir: home } : null;
}

/** Whether a respawn at structure `s` would be honoured for `team` now. */
function openAt(state: GameState, team: number, s: StructureState): boolean {
  if (s.health <= 0 || structureTeam(state, s) !== team || !isLinked(state, s)) return false;
  if (s.type === 'relay' && (s.drops ?? 0) <= 0) return false;
  return spawnSiteRefusal(state, team, s) === null;
}

/** Why `at` can't be `team`'s pick (null: it can). A place lost later is not refused: the pick waits for it. */
export function respawnPickRefusal(state: GameState, team: number, at: RespawnPick): string | null {
  if (at === 'base') return basePlace(state, team) ? null : 'No base to come back at';
  const s = state.structures.find(x => x.id === at.structureId);
  if (!s || (s.type !== 'relay' && s.type !== 'fob')) return 'Pick the base, a relay or a FOB';
  if (structureTeam(state, s) !== team) return 'Not your side’s';
  return null;
}

/** The state with seat `actor`'s pick set (null clears it; the record absent once no seat picks). */
export function applyRespawnPick(state: GameState, actor: number, at: RespawnPick | null): GameState {
  const { [actor]: _was, ...rest } = state.respawnPicks ?? {};
  const next = at === null ? rest : { ...rest, [actor]: at };
  const { respawnPicks: _old, ...base } = state;
  return Object.keys(next).length ? { ...base, respawnPicks: next } : base as GameState;
}

/**
 * Where `unit`, held by seat `actor`, comes back as the `k`-th of its wave, by the seat's pick — or null to fall back to
 * the rule (no pick, or the picked place lost, cut, or in the sector in play). `relayId` names a relay whose drop it spends.
 */
export function pickedSpot(state: GameState, actor: number, unit: Pick<PlayerState, 'teamIndex'>, k: number): { planetIndex: number; surfaceDir: Vec3; relayId?: number } | null {
  const at = state.respawnPicks?.[actor];
  if (at === undefined) return null;
  if (at === 'base') {
    const b = basePlace(state, unit.teamIndex);
    return b ? beaconArrival(state, b, k) : null;
  }
  const s = state.structures.find(x => x.id === at.structureId);
  if (!s || !openAt(state, unit.teamIndex, s)) return null;
  return { ...beaconArrival(state, s, k), ...(s.type === 'relay' ? { relayId: s.id } : {}) };
}

/** The places `team`'s seat may pick, base first then its relays and FOBs by id, each with whether it is open now. */
export function respawnPlaces(state: GameState, team: number): RespawnPlace[] {
  const out: RespawnPlace[] = [];
  const b = basePlace(state, team);
  if (b) out.push({ kind: 'base', ...b, open: true });
  for (const s of [...state.structures].sort((x, y) => x.id - y.id)) {
    if ((s.type !== 'relay' && s.type !== 'fob') || s.health <= 0 || structureTeam(state, s) !== team) continue;
    out.push({ kind: s.type, structureId: s.id, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, open: openAt(state, team, s) });
  }
  return out;
}

/**
 * The pick of `team`'s human seat (its actor is -1 - team, match/input.ts seatActor), with its place and whether it is
 * honoured now; null with no pick. For the table.
 */
export function seatRespawn(state: GameState, team: number): (RespawnPlace & { pick: RespawnPick }) | null {
  const pick = state.respawnPicks?.[-1 - team];
  if (pick === undefined) return null;
  const place = respawnPlaces(state, team).find(x => (pick === 'base' ? x.kind === 'base' : x.structureId === pick.structureId));
  if (place) return { ...place, pick };
  // the picked relay or FOB is down: no place to draw, the rule meanwhile
  const s = pick === 'base' ? undefined : state.structures.find(x => x.id === pick.structureId);
  return s ? { kind: s.type === 'fob' ? 'fob' : 'relay', structureId: s.id, planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, open: false, pick } : null;
}

/** 68.33: a place's card at the table — what it is, how it stands, what it holds, and the seat's pick there. */
export interface PlaceCard {
  kind: 'base' | 'relay' | 'fob';
  structureId?: number;
  label: string;
  /** linked to the base's web, cut from it, or down (the base is always linked) */
  status: 'linked' | 'cut' | 'down';
  /** a relay's drops left */
  drops?: number;
  /** a FOB's crate */
  crate?: { bandages: number; magazines: number };
  /** whether a respawn there would be honoured now, and whether it is the seat's pick */
  open: boolean;
  picked: boolean;
}

/** The card of `team`'s place `at` (its base, or a relay or FOB of its own), or null when it is not one. */
export function placeCard(state: GameState, team: number, at: RespawnPick): PlaceCard | null {
  const mine = state.respawnPicks?.[-1 - team];
  const picked = mine !== undefined && (at === 'base' ? mine === 'base' : mine !== 'base' && mine.structureId === at.structureId);
  if (at === 'base') return basePlace(state, team) ? { kind: 'base', label: 'Your base', status: 'linked', open: true, picked } : null;
  const s = state.structures.find(x => x.id === at.structureId);
  if (!s || (s.type !== 'relay' && s.type !== 'fob') || structureTeam(state, s) !== team) return null;
  const status = s.health <= 0 ? 'down' : isLinked(state, s) ? 'linked' : 'cut';
  return {
    kind: s.type, structureId: s.id, label: `${s.type === 'fob' ? 'FOB' : 'Relay'} ${s.id}`, status,
    ...(s.type === 'relay' ? { drops: s.drops ?? 0 } : {}),
    ...(s.type === 'fob' && s.supply ? { crate: { bandages: s.supply.bandages, magazines: s.supply.magazines } } : {}),
    open: s.health > 0 && openAt(state, team, s), picked,
  };
}
